import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import type { ConnectionCancellationPolicies } from '@luxuryescapes/lib-refunds'
import { formatDate } from '@luxuryescapes/lib-refunds'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import { Refundable, getRefundDescriptions } from '../utils/getCancellationPolicyStatus'
import { getConnectionRefundPolicyDetails } from '../utils/getAlwaysOnConnectionRefundPolicyDetails'
import ConnectionCancellationPolicyModal from './ConnectionCancellationPolicyModal'
import ModalContext from 'contexts/ModalContext'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  cancellationPolicies: ConnectionCancellationPolicies
  cancellationPolicy?: App.OfferCancellationPolicy
  checkIn?: string
  className?: string
  hideNonRefundableMessage?: boolean
  textLinkFormat?: boolean
  hideExcludesFlights?: boolean
  offerType?: string
}

function ConnectionCancellationPoliciesComponent(props: Props) {
  const { cancellationPolicies, cancellationPolicy, className, checkIn, hideNonRefundableMessage, textLinkFormat, hideExcludesFlights, offerType } = props
  const { deadline, partiallyRefundableUntil, refundable, timezone } = getConnectionRefundPolicyDetails(cancellationPolicies, checkIn)

  const refundDescriptions = useMemo(() => getRefundDescriptions(offerType), [offerType])

  const showModal = useContext(ModalContext)

  const onClickIcon = useCallback(() => {
    showModal(<ConnectionCancellationPolicyModal
      title={refundDescriptions[refundable]}
      hideExcludesFlights={!!hideExcludesFlights}
      cancellationPolicyLines={cancellationPolicies.description}
      cancellationPolicyType={cancellationPolicy?.type}
    />)
  }, [showModal, refundable, refundDescriptions, hideExcludesFlights, cancellationPolicies, cancellationPolicy])

  return (
    <Clickable onClick={onClickIcon} className={className}>
      {!hideNonRefundableMessage && (
        <>
          {refundable === Refundable.Fully && (
            <>
              {textLinkFormat &&
                <TextLink
                  size="medium"
                  colour="highlight-secondary"
                  underline={false}
                  startIcon={<LineRefundIcon />}
                >
                  {refundDescriptions[refundable]}
                </TextLink>}
              {!textLinkFormat && (
                <>
                  <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">{refundDescriptions[refundable]}</BodyText>{' '}
                  {deadline &&
                    <BodyText variant="medium" as="span">until {formatDate.formattedShortDateByTimezone(deadline, timezone)} (Hotel only)</BodyText>}
                  {' '}<MiddledTooltipIcon size="XS" />
                </>
              )}
            </>
          )}
          {refundable === Refundable.Partial && (
            <>
              {textLinkFormat &&
                <TextLink
                  size="medium"
                  colour="highlight-secondary"
                  underline={false}
                  startIcon={<LineRefundIcon />}
                >
                  {refundDescriptions[refundable]}
                </TextLink>}
              {!textLinkFormat && (
                <>
                  <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">Partially refundable</BodyText>{' '}
                  {partiallyRefundableUntil &&
                    <BodyText variant="medium" as="span">until {formatDate.formattedShortDateByTimezone(partiallyRefundableUntil, timezone)} (Hotel only)</BodyText>}
                  {' '}<MiddledTooltipIcon size="XS" />
                </>
              )}
            </>
          )}
          {refundable === Refundable.Non && (
            <>
              <BodyText variant="medium" as="span">{refundDescriptions[refundable]}</BodyText>
              {' '}<MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
      {hideNonRefundableMessage && refundable === Refundable.Fully && (
        <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              colour="highlight-secondary"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              Free cancellation available
            </TextLink>}
          {!textLinkFormat && (
            <>
              <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">Free cancellation available</BodyText>{' '}
              {deadline &&
                <BodyText variant="medium" as="span"> until {formatDate.formattedShortDateByTimezone(deadline, timezone)} (Hotel only)</BodyText>}
              {' '}<MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
    </Clickable>
  )
}

export default ConnectionCancellationPoliciesComponent
